// Partner/agency logo manager for the Branding page. Admins upload up to four
// logos (PNG/JPEG/SVG — SVGs are rasterized to PNG server-side), give each one
// required alt text and an optional click-through link, reorder them, and delete
// them. The live order here is exactly the order shown on the landing page,
// intake header, and outgoing emails (next to the fixed Sanborn mark).
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, IconButton, Alert, Tooltip, CircularProgress,
  Paper, Stack, TextField,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import {
  fetchAdminLogos, uploadLogo, patchLogo, deleteLogo, reorderLogos,
  LOGO_MAX_BYTES, LOGO_MAX_COUNT, ALLOWED_LOGO_TYPES,
} from '../../../store/admin-logos-slice.js';
import { logoSrc } from '../../LogoBar.jsx';

const ACCEPT = ALLOWED_LOGO_TYPES.join(',');

// A neutral checkered surface so transparent / white logos stay visible in the
// preview regardless of the admin's theme.
const previewSurfaceSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 132,
  height: 72,
  flexShrink: 0,
  p: 1,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  backgroundColor: '#fff',
  backgroundImage: 'linear-gradient(45deg, #e9e9e9 25%, transparent 25%), '
    + 'linear-gradient(-45deg, #e9e9e9 25%, transparent 25%), '
    + 'linear-gradient(45deg, transparent 75%, #e9e9e9 75%), '
    + 'linear-gradient(-45deg, transparent 75%, #e9e9e9 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
};

// One existing logo: preview + editable alt/href with a per-row Save, plus
// reorder and delete controls. Local edit state is seeded from the row and
// reset whenever the server copy changes.
const LogoRow = ({
  logo, index, count, busy, onMove,
}) => {
  const dispatch = useDispatch();
  const [altText, setAltText] = useState(logo.alt_text || '');
  const [href, setHref] = useState(logo.href || '');

  useEffect(() => {
    setAltText(logo.alt_text || '');
    setHref(logo.href || '');
  }, [logo.alt_text, logo.href]);

  const dirty = altText.trim() !== (logo.alt_text || '')
    || href.trim() !== (logo.href || '');
  const canSave = dirty && altText.trim() !== '';

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'flex-start' } }}>
        <Box sx={previewSurfaceSx}>
          <Box
            component="img"
            src={logoSrc(logo)}
            alt={logo.alt_text || ''}
            sx={{
              maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block',
            }}
          />
        </Box>

        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
          <TextField
            label="Alt text (required)"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            size="small"
            fullWidth
            required
            error={altText.trim() === ''}
            helperText={altText.trim() === '' ? 'Describe the logo, e.g. the full agency name.' : ' '}
          />
          <TextField
            label="Link URL (optional)"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            size="small"
            fullWidth
            placeholder="https://example.gov"
            helperText="Where the logo links when clicked. Leave blank for none."
          />
        </Stack>

        <Stack
          direction={{ xs: 'row', sm: 'column' }}
          spacing={0.5}
          sx={{ alignItems: 'center' }}
        >
          <Tooltip title="Move up">
            <span>
              <IconButton size="small" disabled={busy || index === 0} onClick={() => onMove(index, -1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <span>
              <IconButton size="small" disabled={busy || index === count - 1} onClick={() => onMove(index, 1)}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete logo">
            <span>
              <IconButton size="small" color="error" disabled={busy} onClick={() => dispatch(deleteLogo(logo.id))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {dirty && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            variant="text"
            disabled={busy}
            onClick={() => {
              setAltText(logo.alt_text || '');
              setHref(logo.href || '');
            }}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={busy || !canSave}
            onClick={() => dispatch(patchLogo(logo.id, {
              alt_text: altText.trim(),
              href: href.trim() || null,
            }))}
          >
            Save changes
          </Button>
        </Box>
      )}
    </Paper>
  );
};

const LogoManager = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const {
    items, loaded, loading, busy, actionError,
  } = useSelector((s) => s.adminLogos);

  // Transient staging state for a freshly-picked file awaiting its alt text.
  const [staged, setStaged] = useState(null);

  useEffect(() => { if (!loaded) dispatch(fetchAdminLogos()); }, [dispatch, loaded]);

  const atLimit = items.length >= LOGO_MAX_COUNT;

  const handlePick = (event) => {
    const input = event.target;
    const file = (input.files || [])[0];
    input.value = '';
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setStaged({ error: 'Use a PNG, JPEG, or SVG file.' });
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setStaged({ error: 'File is too large (5 MB max).' });
      return;
    }
    setStaged({
      file, previewUrl: URL.createObjectURL(file), altText: '', href: '',
    });
  };

  const cancelStaged = () => {
    if (staged?.previewUrl) URL.revokeObjectURL(staged.previewUrl);
    setStaged(null);
  };

  const confirmStaged = async () => {
    const result = await dispatch(uploadLogo({
      file: staged.file,
      altText: staged.altText.trim(),
      href: staged.href.trim() || undefined,
    }));
    if (result.ok) cancelStaged();
  };

  const moveBy = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((l) => l.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    dispatch(reorderLogos(ids));
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1,
      }}
      >
        <Typography variant="caption" color="text.secondary">
          {`${items.length} / ${LOGO_MAX_COUNT}`}
        </Typography>
        {(loading || busy) && <CircularProgress size={16} />}
      </Box>

      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {items.map((logo, index) => (
          <LogoRow
            key={logo.id}
            logo={logo}
            index={index}
            count={items.length}
            busy={busy}
            onMove={moveBy}
          />
        ))}
      </Stack>

      {items.length === 0 && !loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No partner logos yet. Add up to
          {` ${LOGO_MAX_COUNT} `}
          to show them next to the Sanborn mark across the portal and emails.
        </Typography>
      )}

      {staged && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          {staged.error ? (
            <Alert severity="error" onClose={cancelStaged}>{staged.error}</Alert>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'flex-start' } }}>
              <Box sx={previewSurfaceSx}>
                <Box
                  component="img"
                  src={staged.previewUrl}
                  alt=""
                  sx={{
                    maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block',
                  }}
                />
              </Box>
              <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                <TextField
                  label="Alt text (required)"
                  value={staged.altText}
                  onChange={(e) => setStaged((s) => ({ ...s, altText: e.target.value }))}
                  size="small"
                  fullWidth
                  required
                  autoFocus
                  error={staged.altText.trim() === ''}
                  helperText="Describe the logo, e.g. the full agency name."
                />
                <TextField
                  label="Link URL (optional)"
                  value={staged.href}
                  onChange={(e) => setStaged((s) => ({ ...s, href: e.target.value }))}
                  size="small"
                  fullWidth
                  placeholder="https://example.gov"
                />
                <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={cancelStaged} disabled={busy}>Cancel</Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={busy || staged.altText.trim() === ''}
                    onClick={confirmStaged}
                  >
                    Upload
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Paper>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        onChange={handlePick}
        style={{ display: 'none' }}
      />
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddPhotoAlternateIcon />}
        disabled={busy || atLimit || Boolean(staged)}
        onClick={() => fileInputRef.current?.click()}
      >
        Add logo
      </Button>
      {atLimit && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {`Maximum of ${LOGO_MAX_COUNT} logos reached.`}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        PNG, JPEG, or SVG · up to 5 MB. SVGs are converted to PNG automatically.
      </Typography>
    </Box>
  );
};

export default LogoManager;
